import { useNavigate } from "@tanstack/react-router";
import CornerDownLeftIcon from "lucide-react/dist/esm/icons/corner-down-left";
import SearchIcon from "lucide-react/dist/esm/icons/search";
import { type KeyboardEvent, useEffect, useMemo, useRef, useState } from "react";

import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

import { NAV_ITEMS, type NavPath } from "./navigation";

type CommandPath = NavPath | "/scenarios/new";

interface Command {
  id: string;
  label: string;
  hint: string;
  group: string;
  to: CommandPath;
  search?: Record<string, unknown>;
}

const COMMANDS: Command[] = [
  ...NAV_ITEMS.map((item) => ({
    id: `nav:${item.to}`,
    label: item.label,
    hint: item.description,
    group: "移動",
    to: item.to
  })),
  {
    id: "filter:critical-alerts",
    label: "未確認の致命アラートだけを見る",
    hint: "アラート · 深刻度=致命 / 未確認",
    group: "絞り込み",
    to: "/alerts",
    search: { severity: "critical", state: "open" }
  },
  {
    id: "filter:paused-scenarios",
    label: "停止中のシナリオを見る",
    hint: "シナリオ · 状態=停止",
    group: "絞り込み",
    to: "/scenarios",
    search: { state: "paused" }
  },
  {
    id: "filter:suppressed-contacts",
    label: "配信抑止中の顧客を見る",
    hint: "顧客 · ステータス=suppressed",
    group: "絞り込み",
    to: "/contacts",
    search: { status: "suppressed" }
  },
  {
    id: "action:new-scenario",
    label: "AIでシナリオを作成する",
    hint: "一文からトリガー・条件・文面を生成",
    group: "操作",
    to: "/scenarios/new"
  },
  {
    id: "filter:urgent-inbox",
    label: "緊急の問い合わせを見る",
    hint: "インボックス · 緊急度=致命",
    group: "絞り込み",
    to: "/inbox",
    search: { urgency: "critical" }
  }
];

interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * 頻出タスクほどクリック数を減らす（原則 7）。
 * ⌘K で移動と定番の絞り込みにキーボードだけで到達できるようにする。
 */
export function CommandPalette({ open, onOpenChange }: CommandPaletteProps) {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const results = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return COMMANDS;
    return COMMANDS.filter((command) =>
      `${command.label} ${command.hint} ${command.group}`.toLowerCase().includes(normalized)
    );
  }, [query]);

  useEffect(() => {
    if (!open) {
      setQuery("");
      setActiveIndex(0);
      return;
    }
    inputRef.current?.focus();
  }, [open]);

  const run = (command: Command | undefined) => {
    if (!command) return;
    onOpenChange(false);
    void navigate({ to: command.to, search: command.search ?? {} });
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((index) => Math.min(index + 1, results.length - 1));
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((index) => Math.max(index - 1, 0));
      return;
    }
    if (event.key === "Enter") {
      event.preventDefault();
      run(results[activeIndex]);
    }
  };

  let lastGroup = "";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent showCloseButton={false} className="gap-0 p-0 sm:max-w-lg">
        <DialogTitle className="sr-only">コマンドパレット</DialogTitle>
        <DialogDescription className="sr-only">
          画面の移動と定番の絞り込みを検索して実行します。
        </DialogDescription>

        <div className="flex h-11 items-center gap-2 border-b border-line px-3">
          <SearchIcon className="size-4 shrink-0 text-fg-subtle" aria-hidden="true" />
          {/* 開いた瞬間に入力できることが要件なので、マウント時にフォーカスを移す */}
          <input
            ref={inputRef}
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setActiveIndex(0);
            }}
            onKeyDown={handleKeyDown}
            placeholder="画面を移動、または絞り込みを実行…"
            aria-label="コマンドを検索"
            className="h-full w-full bg-transparent text-sm outline-none placeholder:text-fg-subtle"
          />
        </div>

        <ul className="max-h-80 overflow-y-auto p-1.5">
          {results.length === 0 ? (
            <li className="px-3 py-6 text-center text-sm text-fg-muted">
              一致するコマンドがありません
            </li>
          ) : (
            results.map((command, index) => {
              const showGroup = command.group !== lastGroup;
              lastGroup = command.group;

              return (
                <li key={command.id}>
                  {showGroup ? <p className="eyebrow px-2 pt-2 pb-1">{command.group}</p> : null}
                  <button
                    type="button"
                    onMouseEnter={() => setActiveIndex(index)}
                    onClick={() => run(command)}
                    className={cn(
                      "flex w-full items-center gap-3 rounded-md px-2 py-1.5 text-left transition-colors",
                      index === activeIndex ? "bg-surface-hover" : "hover:bg-surface-hover"
                    )}
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm text-fg">{command.label}</span>
                      <span className="block truncate text-xs text-fg-subtle">{command.hint}</span>
                    </span>
                    {index === activeIndex ? (
                      <CornerDownLeftIcon
                        className="size-3.5 shrink-0 text-fg-subtle"
                        aria-hidden="true"
                      />
                    ) : null}
                  </button>
                </li>
              );
            })
          )}
        </ul>
      </DialogContent>
    </Dialog>
  );
}
